import { ReactNode, useRef } from 'react';
import ComboBoxField from '@/components/select/ComboBoxField';
import { MultiComboBoxProps } from '@/components/select/comboBoxTypes';
import useComboBoxQuery from '@/components/select/useComboBoxQuery';
import useMultiSelectBox from '@/components/select/useMultiSelectBox';
import { Combobox } from '@headlessui/react';

const MultiComboBox = <T extends { id: string; name: string }>({
  name,
  value,
  label,
  hideLabel,
  placeholder,
  queryKey = name ? [name] : [],
  queryFn,
  onSelect,
  allowEmptySelection = true,
  requireSelection,
  disabled = false,
  delay = 500,
  closeOnBlur = true,
  openOnFocus = true,
  showEmptyState = false,
}: MultiComboBoxProps<T>): ReactNode => {
  const isInitializedRef = useRef(false);
  const comboBox = useComboBoxQuery<T>({
    queryKey,
    queryFn,
    isInitializedRef,
    delay,
    closeOnBlur,
    openOnFocus,
  });
  const selection = useMultiSelectBox<T>(comboBox.items, { value, onSelect });
  isInitializedRef.current = selection.isInitialized;

  return (
    <Combobox
      value={selection.selected}
      onChange={selection.handleChange}
      disabled={disabled}
      multiple
    >
      <ComboBoxField<T>
        label={label}
        hideLabel={hideLabel}
        placeholder={placeholder}
        inputRef={comboBox.inputRef}
        open={comboBox.open}
        items={comboBox.items}
        isLoading={comboBox.isLoading}
        allowEmptySelection={allowEmptySelection}
        requireSelection={requireSelection}
        showEmptyOption
        displayValue={() => ''}
        onFocus={comboBox.handleFocus}
        onBlur={comboBox.handleBlur}
        onAfterLeave={comboBox.handleAfterLeave}
        onDeselect={selection.handleDeselect}
        showEmptyState={showEmptyState}
      />
    </Combobox>
  );
};

export default MultiComboBox;
